import { useEffect, useState } from 'react';

import useSWRInfinite from 'swr/infinite';

// Utils
import { get } from '@/utils/client';

// API Endpoints
import { CAPTION_LIST } from '@/lib/routes/apiEndpoints';

const getKey = (pageIndex, previousPageData, activeSPID, limit, requestParams) => {
    // No request params
    if (!requestParams) return null;

    // Reached the end
    if (previousPageData && !previousPageData) return null;

    // First page, we don't have `previousPageData`
    if (pageIndex === 0)
        return [
            `${CAPTION_LIST.replace('%SPID%', activeSPID)}?per_page=${limit}`,
            JSON.stringify(requestParams)
        ];

    // Add the cursor to the API endpoint
    return [
        `${CAPTION_LIST.replace('%SPID%', activeSPID)}?per_page=${limit}&cursor=${
            previousPageData.cursor
        }`,
        JSON.stringify(requestParams)
    ];
};

const useCaptionsHashtags = ({ activeSPID, limit = 20, params = null, options = {} } = {}) => {
    const [requestParams, setRequestParams] = useState(params);
    const [activeSocialProfile, setActiveSocialProfile] = useState(activeSPID);

    useEffect(() => {
        setActiveSocialProfile(activeSPID);
    }, [activeSPID]);

    const {
        data: dataCaptionsHashtags,
        size,
        setSize,
        mutate: mutateCaptionsHashtags,
        error: captionsHashtagsError
    } = useSWRInfinite(
        (pageIndex, previousPageData) =>
            getKey(pageIndex, previousPageData, activeSocialProfile, limit, requestParams),
        ([url]) => get(url, requestParams).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    const isLoadingInitialData = !dataCaptionsHashtags && !captionsHashtagsError;
    const isLoadingMore =
        size > 0 && dataCaptionsHashtags && typeof dataCaptionsHashtags[size - 1] === 'undefined';

    const captionsHashtags = dataCaptionsHashtags?.flatMap(({ data }) => data) || [];

    return {
        captionsHashtags,
        loadMoreCaption: () => setSize(size + 1), // Function to load more media (load more button)
        mutateCaptionsHashtags,
        captionsHashtagsError,
        requestParams,
        setRequestParams, // Setter to change parameters in the url
        setActiveSocialProfile,
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        canLoadMore:
            dataCaptionsHashtags &&
            typeof dataCaptionsHashtags[dataCaptionsHashtags.length - 1].cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};

export default useCaptionsHashtags;
